import React, { useState, useEffect } from 'react';
import { scrollToTop } from "../utils/scrollUtils";
import styled from 'styled-components';
import { motion } from 'framer-motion';

interface FooterProps {
  t: any;
  isRTL: boolean;
}

const FooterSection = styled.footer`
  background: linear-gradient(135deg, #1e293b 0%, #334155 100%);
  color: white;
  position: relative;
  border-top: 3px solid #dc2626;
`;

const Container = styled.div<{ $isRTL: boolean }>`
  max-width: 100%;
  margin: 0 auto;
  padding: 2rem 1rem;
  direction: ${props => props.$isRTL ? 'rtl' : 'ltr'};
  
  @media (max-width: 768px) {
    padding: 1.5rem 1rem;
  }
`;

const FooterContent = styled.div<{ $isRTL: boolean }>`
  display: grid;
  grid-template-columns: 2fr 1fr 1fr;
  gap: 2rem;
  margin-bottom: 1.5rem;
  text-align: ${props => props.$isRTL ? 'right' : 'left'};
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 2rem;
    text-align: center;
  }
`;

const BrandSection = styled.div<{ $isRTL: boolean }>`
  h3 {
    font-size: 1.8rem;
    font-weight: 700;
    color: white;
    margin-bottom: 0.5rem;
    letter-spacing: 1px;
    font-family: ${props => props.$isRTL ? 
      "'Fustat', 'Amiri', 'Noto Sans Arabic', 'Arial', sans-serif" : 
      "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif"};
    text-align: ${props => props.$isRTL ? 'right' : 'left'};
    
    @media (max-width: 768px) {
      font-size: 1.5rem;
      text-align: center;
    }
  }
  
  p {
    font-size: 0.9rem;
    opacity: 0.8;
    line-height: 1.6;
    margin-bottom: 1rem;
    font-family: ${props => props.$isRTL ? 
      "'Fustat', 'Amiri', 'Noto Sans Arabic', 'Arial', sans-serif" : 
      "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif"};
    text-align: ${props => props.$isRTL ? 'right' : 'left'};
    
    @media (max-width: 768px) {
      text-align: center;
    }
  }
`;

const ContactInfo = styled.div<{ $isRTL: boolean }>`
  h4 {
    font-size: 1.2rem;
    font-weight: 600;
    color: #dc2626;
    margin-bottom: 1rem;
    font-family: ${props => props.$isRTL ? 
      "'Fustat', 'Amiri', 'Noto Sans Arabic', 'Arial', sans-serif" : 
      "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif"};
    text-align: ${props => props.$isRTL ? 'right' : 'left'};
    
    @media (max-width: 768px) {
      text-align: center;
    }
  }
`;

const ContactItem = styled.div<{ $isRTL: boolean }>`
  display: flex;
  align-items: flex-start;
  margin-bottom: 0.8rem;
  font-size: 0.9rem;
  opacity: 0.8;
  flex-direction: ${props => props.$isRTL ? 'row-reverse' : 'row'};
  text-align: ${props => props.$isRTL ? 'right' : 'left'};
  font-family: ${props => props.$isRTL ? 
    "'Fustat', 'Amiri', 'Noto Sans Arabic', 'Arial', sans-serif" : 
    "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif"};
  
  img {
    width: 16px;
    height: 16px;
    ${props => props.$isRTL ? 'margin-left: 0.5rem;' : 'margin-right: 0.5rem;'}
    filter: brightness(0) invert(1);
    flex-shrink: 0;
    margin-top: 2px;
    object-fit: contain;
  }
  
  span {
    flex: 1;
    line-height: 1.4;
  }
  
  a {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    color: inherit;
    text-decoration: none;
    cursor: pointer;
    flex-direction: ${props => props.$isRTL ? 'row-reverse' : 'row'};
  }
`;

const EmailLink = styled.a<{ $isRTL: boolean }>`
  flex: 1;
  line-height: 1.4;
  color: inherit;
  text-decoration: none;
  transition: all 0.3s ease;
  
  &:hover {
    color: #dc2626;
    text-decoration: underline;
  }
`;

const WhatsAppContactItem = styled.a<{ $isRTL: boolean }>`
  display: flex;
  align-items: flex-start;
  margin-bottom: 0.8rem;
  font-size: 0.9rem;
  opacity: 0.8;
  flex-direction: ${props => props.$isRTL ? 'row-reverse' : 'row'};
  text-align: ${props => props.$isRTL ? 'right' : 'left'};
  font-family: ${props => props.$isRTL ? 
    "'Fustat', 'Amiri', 'Noto Sans Arabic', 'Arial', sans-serif" : 
    "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif"};
  text-decoration: none;
  color: white;
  transition: all 0.3s ease;
  
  &:hover {
    opacity: 1;
    color: #25D366;
    transform: translateX(${props => props.$isRTL ? '-2px' : '2px'});
  }
  
  img {
    width: 16px;
    height: 16px;
    ${props => props.$isRTL ? 'margin-left: 0.5rem;' : 'margin-right: 0.5rem;'}
    filter: brightness(0) invert(1);
    flex-shrink: 0;
    transition: filter 0.3s ease;
    margin-top: 2px;
    object-fit: contain;
  }
  
  &:hover img {
    filter: none;
  }
  
  span {
    flex: 1;
    line-height: 1.4;
  }
  
  @media (max-width: 768px) {
    justify-content: center;
  }
`;

// Special component for phone numbers in footer
const PhoneSpan = styled.span<{ $isRTL: boolean }>`
  direction: ltr;
  text-align: ${props => props.$isRTL ? 'right' : 'left'};
  font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
`;

const SocialSection = styled.div<{ $isRTL: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: ${props => props.$isRTL ? 'flex-end' : 'flex-start'};
  
  h4 {
    font-size: 1.2rem;
    font-weight: 600;
    color: #dc2626;
    margin-bottom: 1rem;
    font-family: ${props => props.$isRTL ? 
      "'Fustat', 'Amiri', 'Noto Sans Arabic', 'Arial', sans-serif" : 
      "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif"};
    text-align: ${props => props.$isRTL ? 'right' : 'left'};
    
    @media (max-width: 768px) {
      text-align: center;
    }
  }
  
  @media (max-width: 768px) {
    align-items: center;
  }
`;

const SocialLinks = styled.div<{ $isRTL: boolean }>`
  display: flex;
  gap: 1rem;
  flex-direction: ${props => props.$isRTL ? 'row-reverse' : 'row'};
  
  @media (max-width: 768px) {
    justify-content: center;
  }
`;

const SocialLink = styled.a`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  background: rgba(220, 38, 38, 0.1);
  border: 1px solid rgba(220, 38, 38, 0.3);
  border-radius: 50%;
  transition: all 0.3s ease;
  
  img {
    width: 20px;
    height: 20px;
    filter: brightness(0) invert(1);
  }
  
  &:hover {
    background: #dc2626;
    transform: translateY(-2px);
    box-shadow: 0 4px 15px rgba(220, 38, 38, 0.3);
  }
`;

const FooterBottom = styled.div<{ $isRTL: boolean }>`
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  padding-top: 1.5rem;
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 0.85rem;
  opacity: 0.7;
  flex-direction: ${props => props.$isRTL ? 'row-reverse' : 'row'};
  text-align: center;
  
  @media (max-width: 768px) {
    flex-direction: column;
    text-align: center;
  }
`;

const Copyright = styled.p<{ $isRTL: boolean }>`
  margin: 0;
  font-family: ${props => props.$isRTL ? 
    "'Fustat', 'Amiri', 'Noto Sans Arabic', 'Arial', sans-serif" : 
    "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif"};
  text-align: center;
`;

const ScrollToTopButton = styled(motion.button)<{ $isRTL: boolean }>`
  position: fixed;
  bottom: 2rem;
  ${props => props.$isRTL ? 'left: 2rem;' : 'right: 2rem;'}
  width: 50px;
  height: 50px;
  background: linear-gradient(135deg, #dc2626 0%, #b91c1c 100%);
  color: white;
  border: none;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.2rem;
  box-shadow: 0 4px 15px rgba(220, 38, 38, 0.3);
  z-index: 1000;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(220, 38, 38, 0.4);
  }
  
  @media (max-width: 768px) {
    bottom: 1rem;
    ${props => props.$isRTL ? 'left: 1rem;' : 'right: 1rem;'}
    width: 45px;
    height: 45px;
  }
`;

const Footer: React.FC<FooterProps> = ({ t, isRTL }) => {
  const [showScrollTop, setShowScrollTop] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 300);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleScrollToTop = () => {
    scrollToTop();
  };

  return (
    <FooterSection>
      <Container $isRTL={isRTL}>
        <FooterContent $isRTL={isRTL}>
          <BrandSection $isRTL={isRTL}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
              <img 
                src="/images/logo192.png" 
                alt="TECHYID Logo" 
                style={{
                  filter: 'brightness(0) invert(1)',
                  height: '40px',
                  width: 'auto'
                }}
              />
              <img 
                src="/images/text.png" 
                alt="TECHYID" 
                style={{
                  filter: 'brightness(0) invert(1)',
                  height: '40px',
                  width: 'auto'
                }}
              />
            </div>
            <p>{t.slogan}</p>
          </BrandSection>
          
          <SocialSection $isRTL={isRTL}>
            <h4>{t.followUs}</h4>
            <SocialLinks $isRTL={isRTL}>
              <SocialLink href="https://www.facebook.com/profile.php?id=61578814623072" target="_blank" rel="noopener noreferrer">
                <img src="/images/footer/facebook.svg" alt={t.altTexts.facebook} />
              </SocialLink>
              <SocialLink href="https://wa.me/22248610689" target="_blank" rel="noopener noreferrer">
                <img src="/images/footer/whatsapp.svg" alt="WhatsApp" />
              </SocialLink>
            </SocialLinks>
          </SocialSection>
          
          <ContactInfo $isRTL={isRTL}>
            <h4>{t.footerContact}</h4>
            <ContactItem $isRTL={isRTL}>
              <a 
                href="https://maps.app.goo.gl/utrwQPXAnSzFLL8t5"
                target="_blank"
                rel="noopener noreferrer"
                style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '0.5rem', 
                  color: 'inherit', 
                  textDecoration: 'none',
                  cursor: 'pointer'
                }}
              >
                <img src="/images/footer/location.svg" alt={t.altTexts.location} />
                <span>{t.address}</span>
              </a>
            </ContactItem>
            <ContactItem $isRTL={isRTL}>
              <img src="/images/footer/phone.svg" alt={t.altTexts.phone} />
              <PhoneSpan $isRTL={isRTL}>{t.phone}</PhoneSpan>
            </ContactItem>
            <WhatsAppContactItem 
              $isRTL={isRTL}
              href="https://wa.me/22248610689"
              target="_blank"
              rel="noopener noreferrer"
            >
              <img src="/images/footer/whatsapp.svg" alt="WhatsApp" />
              <PhoneSpan $isRTL={isRTL}>+222 48 61 06 89</PhoneSpan>
            </WhatsAppContactItem>
            <ContactItem $isRTL={isRTL}>
              <a 
                href="mailto:contact@techyid.com"
                style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '0.5rem', 
                  color: 'inherit', 
                  textDecoration: 'none',
                  cursor: 'pointer'
                }}
              >
                <img src="/images/footer/mail.svg" alt={t.altTexts.email} />
                <span>{t.email}</span>
              </a>
            </ContactItem>
            <ContactItem $isRTL={isRTL}>
              <img src="/images/footer/time.svg" alt={t.altTexts.time} />
              <span>
                {t.workingHours}
              </span>
            </ContactItem>
          </ContactInfo>
        </FooterContent>
        
        <FooterBottom $isRTL={isRTL}>
          <Copyright $isRTL={isRTL}>{t.footerText}</Copyright>
        </FooterBottom>
      </Container>
      
      {showScrollTop && (
        <ScrollToTopButton
          $isRTL={isRTL}
          onClick={handleScrollToTop}
          initial={{ opacity: 0, scale: 0 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0 }}
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
        >
          ↑
        </ScrollToTopButton>
      )}
    </FooterSection>
  );
};

export default Footer;